import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { useAuth } from '@/contexts/AuthContext';
import { LoginPage } from '@/pages/Auth/LoginPage';
import { RegisterPage } from '@/pages/Auth/RegisterPage';
import { PasswordResetPage } from '@/pages/Auth/PasswordResetPage';
import { ProfilePage } from '@/pages/Userprofile/ProfilePage';
import { MyProfilesPage } from '@/pages/Userprofile/MyProfilesPage';
import { SearchProfilesPage } from '@/pages/Userprofile/SearchProfilesPage';
import { CreateEditProfilePage } from '@/pages/Userprofile/CreateEditProfilePage';
import { BalancePage } from '../pages/Balance/BalancePage';
import { MachinePage } from '../pages/Machine/MachinePage';
import { SettingsPage } from '../pages/Settings/SettingsPage';
import { TeamPage } from '../pages/Team/TeamPage';
import { UserPage } from '../pages/User/UserPage';
import { MachinesPage } from '../pages/Machine/MachinesPage';
import { PlayLobbyPage } from '../pages/Machine/PlayLobbyPage';
import { PlayCabinetPage } from '../pages/Machine/PlayCabinetPage';
import { UserSettingsPage } from '../pages/Notifications/UserSettingsPage';
import { TemplatesPage } from '../pages/Notifications/TemplatesPage';
import { ChannelsPage } from '../pages/Notifications/ChannelsPage';
import { MyTeamsPage } from '../pages/Team/MyTeamsPage';
import { AllTeamsPage } from '../pages/Team/AllTeamsPage';
import { TeamRequestsPage } from '../pages/Team/TeamRequestsPage';
import { CreateTeamPage } from '../pages/Team/CreateTeamPage';
import { 
  VoiceRoomsPage, 
  VoiceGroupsPage, 
  VoiceGroupPage,
  VoiceRoomSettingsPage,
  VoiceGroupSettingsPage,
  VoiceRoomEditPage
} from '../pages/Voice';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  console.log('[ProtectedRoute] Auth state:', { isAuthenticated, isLoading });

  if (isLoading) {
    console.log('[ProtectedRoute] Auth loading, showing spinner');
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('[ProtectedRoute] Authenticated, rendering children');
  return <>{children}</>;
};

interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/profile" replace />;
  }

  return <>{children}</>;
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Default route - redirect to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* Public routes - accessible without authentication */}
      <Route path="/login" element={
        <ErrorBoundary>
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        </ErrorBoundary>
      } />
      <Route path="/register" element={
        <ErrorBoundary>
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        </ErrorBoundary>
      } />
      <Route path="/password-reset" element={
        <ErrorBoundary>
          <PublicRoute>
            <PasswordResetPage />
          </PublicRoute>
        </ErrorBoundary>
      } />

      {/* Protected routes - require authentication */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<ProfilePage />} />
        <Route path="edit/:profileId" element={<CreateEditProfilePage />} />
        <Route path="create" element={<CreateEditProfilePage />} />
      </Route>

      <Route path="/profiles" element={<Navigate to="/profiles/my-profiles" replace />} />
      <Route path="/profiles/my-profiles" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <MyProfilesPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/profiles/search" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <SearchProfilesPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/balance" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <BalancePage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      {/* Player-facing machines experience */}
      <Route path="/play" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <PlayLobbyPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/play/:machineId" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <PlayCabinetPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      {/* Admin-facing machines management */}
      <Route path="/machine" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              {console.log('[AppRoutes] Rendering MachinesPage route')}
              <MachinesPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/machine/:machineId" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <MachinePage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="account" replace />} />
        <Route path="account" element={<SettingsPage />} />
        <Route path="profile" element={<SettingsPage />} />
        <Route path="privacy" element={<SettingsPage />} />
        <Route path="ui" element={<SettingsPage />} />
        <Route path="notifications" element={<SettingsPage />} />
      </Route>
      <Route path="/teams" element={<Navigate to="/teams/my-teams" replace />} />
      <Route path="/teams/my-teams" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <MyTeamsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/teams/all-teams" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <AllTeamsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/teams/requests" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <TeamRequestsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/teams/create" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <CreateTeamPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/teams/:teamId" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <TeamPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/user" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <UserPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Voice routes */}
      <Route path="/voice" element={<Navigate to="/voice/rooms" replace />} />
      <Route path="/voice/rooms" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <VoiceRoomsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/voice/rooms/:roomId" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <VoiceGroupsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/voice/groups/:groupId" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <VoiceGroupPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/voice/rooms/:roomId/edit" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <VoiceRoomEditPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/voice/rooms/:roomId/settings" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <VoiceRoomSettingsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/voice/groups/:groupId/settings" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <VoiceGroupSettingsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Notification routes */}
      <Route path="/notifications" element={<Navigate to="/notifications/user-settings" replace />} />
      <Route path="/notifications/user-settings" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <UserSettingsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/notifications/templates" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <TemplatesPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/notifications/channels" element={
        <ProtectedRoute>
          <Layout>
            <ErrorBoundary>
              <ChannelsPage />
            </ErrorBoundary>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Legacy routes for backward compatibility */}
      <Route path="/userprofile" element={<Navigate to="/profile" replace />} />
      <Route path="/dashboard" element={<Navigate to="/profile" replace />} />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}; 